import React, { useState } from "react";
import "./CompetitionSubmitForm.css";
import { ref, uploadBytesResumable, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { storage, db } from "./firebase";

// helper: create URL-friendly slugs (used for storage paths)
const slugify = (s = "") =>
  s
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]/g, "");

// TEMP: Firebase Storage quota exceeded. Set to true to restore direct file uploads.
const FILE_UPLOADS_ENABLED = false;

const gradeOptions = ["6", "7", "8", "9", "10", "11", "After O/L", "12", "13"];

export default function CompetitionSubmitForm({ competition, onClose }) {
  const compTitle = (competition?.title || "").trim();

  // form state (generic)
  const [values, setValues] = useState({});
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // upload progress state (percent per field key)
  const [uploadProgress, setUploadProgress] = useState({});

  // success overlay state
  const [showSuccess, setShowSuccess] = useState(false);
  const [successId, setSuccessId] = useState(null);

  // helpers
  const sanitizeUrl = (url = "") => {
    try {
      const cleaned = url.trim();
      if (!/^https?:\/\//i.test(cleaned)) return "";
      // basic block for javascript: and data: uris
      if (/^\s*javascript:|^\s*data:/i.test(cleaned)) return "";
      return cleaned;
    } catch {
      return "";
    }
  };

  const maxLen = (s = "", n = 200) => (s ? s.substring(0, n) : "");

  // field schemas per competition (simple, driven by the user's spec)
  const getFields = (title) => {
    const t = title.toLowerCase();
    if (t.includes("web development")) {
      return [
        { key: "name", label: "Name", type: "text", required: true },
        { key: "school", label: "School", type: "text", required: true },
        { key: "grade", label: "Grade", type: "select", options: gradeOptions, required: true },
        { key: "github", label: "Github Repo Link (public)", type: "url", required: true, sanitize: true },
        { key: "hosted", label: "Hosted Link", type: "url", required: true, sanitize: true },
        { key: "copyright", label: "I haven't used copyrighted materials without mentioning", type: "checkbox", required: true },
      ];
    }
    if (t.includes("algorithm")) {
      return [
        { key: "name", label: "Name", type: "text", required: true },
        { key: "school", label: "School", type: "text", required: true },
        { key: "grade", label: "Grade", type: "select", options: gradeOptions, required: true },
        { key: "email", label: "Email (for HackerRank)", type: "email", required: true },
        { key: "hr_username", label: "HackerRank Username", type: "text", required: true },
        { key: "language", label: "Preferred Language", type: "select", options: ["C++", "Java", "Python", "JavaScript"], required: true },
        { key: "plagiarism", label: "I agree that if involved in plagiarism ...", type: "checkbox", required: true },
      ];
    }
    if (t.includes("real-world") || t.includes("real world")) {
      return [
        { key: "name", label: "Name", type: "text", required: true },
        { key: "school", label: "School", type: "text", required: true },
        { key: "grade", label: "Grade", type: "select", options: gradeOptions, required: true },
        { key: "github_user", label: "Github Username", type: "text", required: true },
        { key: "fork_link", label: "Link to forked repository", type: "url", required: true, sanitize: true },
        { key: "dev_env", label: "Will you develop on your local machine or container?", type: "select", options: ["Local", "Container (Docker)"], required: false },
        { key: "ack", label: "I understand the fork/PR/testing guidelines", type: "checkbox", required: true },
      ];
    }
    if (t.includes("fastest line") || t.includes("line following")) {
      return [
        { key: "name", label: "Name", type: "text", required: true },
        { key: "grade", label: "Grade", type: "select", options: gradeOptions, required: true },
        { key: "school", label: "School", type: "text", required: true },
        { key: "drive_link", label: "Drive folder link (project doc + demo video)", type: "url", required: true, sanitize: true },
        { key: "copyright", label: "I haven't used copyrighted materials without mentioning", type: "checkbox", required: true },
      ];
    }
    if (t.includes("innovation")) {
      return [
        { key: "name", label: "Name", type: "text", required: true },
        { key: "grade", label: "Grade", type: "select", options: gradeOptions, required: true },
        { key: "school", label: "School", type: "text", required: true },
        { key: "drive_link", label: "Google Drive link (doc + video)", type: "url", required: true, sanitize: true },
        { key: "copyright", label: "I haven't used copyrighted materials without mentioning", type: "checkbox", required: true },
      ];
    }
    if (t.includes("photo manipulation")) {
      return [
        { key: "name", label: "Name", type: "text", required: true },
        { key: "grade", label: "Grade", type: "select", options: gradeOptions, required: true },
        { key: "school", label: "School", type: "text", required: true },
        { key: "phone", label: "Phone number", type: "text", required: false },
        { key: "project_files", label: "Project files (.rar)", type: "file", accept: ".rar,.zip", required: true },
        { key: "final_art", label: "Final artwork (.jpg,.png)", type: "file", accept: ".jpg,.jpeg,.png", required: true },
        { key: "copyright", label: "I haven't used copyrighted materials without mentioning", type: "checkbox", required: true },
      ];
    }
    if (t.includes("poster design")) {
      return [
        { key: "name", label: "Name", type: "text", required: true },
        { key: "grade", label: "Grade", type: "select", options: gradeOptions, required: true },
        { key: "school", label: "School", type: "text", required: true },
        { key: "source_file", label: "Source file (.ai,.psd etc.)", type: "file", accept: ".ai,.psd,.xd,.sketch,.zip", required: true },
        { key: "poster_file", label: "Poster file (.jpg,.png)", type: "file", accept: ".jpg,.jpeg,.png", required: true },
        { key: "copyright", label: "I haven't used copyrighted materials without mentioning", type: "checkbox", required: true },
      ];
    }
    if (t.includes("logo design")) {
      return [
        { key: "name", label: "Name", type: "text", required: true },
        { key: "grade", label: "Grade", type: "select", options: gradeOptions, required: true },
        { key: "school", label: "School", type: "text", required: true },
        { key: "final_art", label: "Final artwork (.jpg,.png,.svg,.zip)", type: "file", accept: ".jpg,.jpeg,.png,.svg,.zip", required: true },
        { key: "copyright", label: "I haven't used copyrighted materials without mentioning", type: "checkbox", required: true },
      ];
    }
    if (t.includes("ui/ux") || t.includes("ux design") || t.includes("ui ux")) {
      return [
        { key: "name", label: "Name", type: "text", required: true },
        { key: "grade", label: "Grade", type: "select", options: gradeOptions, required: true },
        { key: "school", label: "School", type: "text", required: true },
        { key: "screens", label: "Interface screen (PNG/JPG)", type: "file", accept: ".jpg,.jpeg,.png", required: true },
        { key: "prototype", label: "Interactive prototype link (Figma/Adobe XD)", type: "url", required: true, sanitize: true },
        { key: "concept", label: "Brief concept note (pdf)", type: "file", accept: ".pdf", required: true },
        { key: "all_files", label: "All project files (.zip)", type: "file", accept: ".zip", required: false },
        { key: "copyright", label: "I haven't used copyrighted materials without mentioning", type: "checkbox", required: true },
      ];
    }

    // default fallback (general)
    return [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "school", label: "School", type: "text", required: true },
      { key: "notes", label: "Notes", type: "textarea", required: false },
      { key: "file", label: "Upload file", type: "file", required: false },
    ];
  };

  // While FILE_UPLOADS_ENABLED is false, file fields are shown as Google Drive link inputs instead.
  const fields = getFields(compTitle).map((f) =>
    f.type === "file" && !FILE_UPLOADS_ENABLED
      ? {
          ...f,
          type: "url",
          sanitize: true,
          fileLink: true,
          label: `${f.label} — Google Drive link (set sharing to "Anyone with the link")`,
          placeholder: "https://drive.google.com/...",
        }
      : f
  );

  // change handlers
  const handleChange = (key, v) => {
    setValues((s) => ({ ...s, [key]: typeof v === "string" ? maxLen(v, 2000) : v }));
  };

  const handleFileChange = (key, e) => {
    const f = e.target.files;
    if (!f) return;
    // store first file for single-file fields; for multi use array
    setFiles((s) => ({ ...s, [key]: f.length > 1 ? Array.from(f) : f[0] }));
  };

  // simple validation before submit
  const validate = () => {
    const errs = [];
    fields.forEach((f) => {
      const v = values[f.key];
      const fileVal = files[f.key];
      if (f.required) {
        if (f.type === "file" && !fileVal) errs.push(`${f.label} is required`);
        if (f.type !== "file" && (v === undefined || v === "" || v === false)) errs.push(`${f.label} is required`);
      }
      if (f.type === "url" && v) {
        if (!sanitizeUrl(v)) errs.push(`${f.label} is not a valid http(s) link`);
      }
    });
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors(null);
    const errs = validate();
    if (errs.length) {
      setErrors(errs);
      return;
    }

    setSubmitting(true);
    try {
      // Build metadata object
      const submission = {
        competition: compTitle,
        school: competition?.school || "",
        values: {},
        files: {},
        createdAt: serverTimestamp(),
      };

      // copy non-file values (sanitize urls when needed)
      Object.entries(values).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        const fieldSchema = fields.find((x) => x.key === k);
        if (fieldSchema?.sanitize && typeof v === "string") {
          submission.values[k] = sanitizeUrl(v);
        } else if (typeof v === "boolean") {
          submission.values[k] = v;
        } else {
          submission.values[k] = maxLen(String(v), 5000);
        }
      });

      // Drive-link replacements for file fields: store under files (same place as uploaded URLs)
      fields.forEach((fld) => {
        if (fld.fileLink && submission.values[fld.key]) {
          submission.files[fld.key] = submission.values[fld.key];
          delete submission.values[fld.key];
        }
      });

      // Upload files to Firebase Storage and capture URLs
      // reset progress
      setUploadProgress({});
      const uploadPromises = Object.entries(files).map(([k, f]) => {
        if (!f) return null;

        // initialize progress for this field
        setUploadProgress((p) => ({ ...p, [k]: 0 }));

        // upload a single File and return the download URL
        const uploadField = (fileItem, index = 0, total = 1) =>
          new Promise((resolve, reject) => {
            const timestamp = Date.now();
            // safe filename
            const safeName = (fileItem.name || "file").replace(/\s+/g, "_").replace(/[^\w\-_.]/g, "");
            const path = `submissions/${slugify(compTitle || "general")}/${timestamp}_${safeName}`;
            const storageRef = ref(storage, path);
            const uploadTask = uploadBytesResumable(storageRef, fileItem);

            uploadTask.on(
              "state_changed",
              (snapshot) => {
                const percent = snapshot.totalBytes ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100) : 0;
                // for multiple files in one field we approximate by averaging
                setUploadProgress((prev) => {
                  const prevVal = prev[k];
                  if (Array.isArray(prevVal)) {
                    const arr = [...prevVal];
                    arr[index] = percent;
                    // compute average
                    const avg = Math.round(arr.reduce((s, x) => s + (x || 0), 0) / arr.length);
                    return { ...prev, [k]: arr.length === 1 ? percent : avg };
                  }
                  // single file field or first time
                  return { ...prev, [k]: percent };
                });
              },
              (error) => reject(error),
              async () => {
                try {
                  const url = await getDownloadURL(uploadTask.snapshot.ref);
                  resolve(url);
                } catch (e) {
                  reject(e);
                }
              }
            );
          });

        if (Array.isArray(f)) {
          // upload multiple files for this field
          return Promise.all(f.map((fileItem, idx) => uploadField(fileItem, idx, f.length))).then((urls) => {
            // store as array and set final progress to 100
            setUploadProgress((p) => ({ ...p, [k]: 100 }));
            return [k, urls.filter(Boolean)];
          });
        } else {
          return uploadField(f).then((url) => {
            setUploadProgress((p) => ({ ...p, [k]: 100 }));
            return [k, url];
          });
        }
      });

      const results = await Promise.all(uploadPromises);
      results.forEach((r) => {
        if (!r) return;
        const [k, v] = r;
        submission.files[k] = v;
      });

      // Save submission metadata to Firestore
      const docRef = await addDoc(collection(db, "submissions"), submission);

      // success: show overlay with reference ID
      setSuccessId(docRef.id);
      setShowSuccess(true);
    } catch (err) {
      console.error("Upload error:", err);
      setErrors(["An unexpected error occurred while uploading."]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="submission-modal">
      <div className="submission-card">
        <div className="submission-header">
          <h2>Submit — {compTitle || "Competition"}</h2>
          <button className="close-btn glass-btn" onClick={onClose} disabled={submitting}>Close</button>
        </div>

        <form className="submission-form" onSubmit={handleSubmit}>
          {errors && (
            <div style={{ color: "#ffcccc", fontSize: 13 }}>
              {errors.map((er, i) => <div key={i}>• {er}</div>)}
            </div>
          )}

          {fields.map((f) => {
            const val = values[f.key] ?? "";
            if (f.type === "text" || f.type === "email" || f.type === "url") {
              return (
                <label key={f.key}>
                  {f.label}{f.required ? " *" : ""}
                  <input
                    type={f.type === "url" ? "text" : f.type}
                    value={val}
                    onChange={(e) => handleChange(f.key, e.target.value)}
                    placeholder={f.placeholder || ""}
                  />
                </label>
              );
            }
            if (f.type === "textarea") {
              return (
                <label key={f.key}>
                  {f.label}{f.required ? " *" : ""}
                  <textarea value={val} onChange={(e) => handleChange(f.key, e.target.value)} />
                </label>
              );
            }
            if (f.type === "select") {
              return (
                <label key={f.key}>
                  {f.label}{f.required ? " *" : ""}
                  <select value={val} onChange={(e) => handleChange(f.key, e.target.value)}>
                    <option value="">{f.required ? "Select..." : "Optional"}</option>
                    {f.options?.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </label>
              );
            }
            if (f.type === "checkbox") {
              return (
                <label key={f.key} style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={!!values[f.key]}
                    onChange={(e) => handleChange(f.key, e.target.checked)}
                  />
                  <span>{f.label}{f.required ? " *" : ""}</span>
                </label>
              );
            }
            if (f.type === "file") {
              return (
                <label key={f.key}>
                  {f.label}{f.required ? " *" : ""}
                  <input
                    type="file"
                    accept={f.accept}
                    onChange={(e) => handleFileChange(f.key, e)}
                  />
                </label>
              );
            }
            // fallback
            return null;
          })}

          {/* upload progress indicators */}
          {submitting && Object.keys(uploadProgress).length > 0 && (
            <div style={{ marginBottom: 12 }}>
              {Object.entries(uploadProgress).map(([k, pct]) => (
                <div key={k} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 12, color: "#ddd", marginBottom: 4 }}>{k.replace(/_/g, " ")}</div>
                  <div style={{ background: "#333", height: 10, borderRadius: 6, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: "#28a745", transition: "width 200ms ease" }} />
                  </div>
                  <div style={{ fontSize: 11, color: "#ccc", marginTop: 4 }}>{pct}%</div>
                </div>
              ))}
            </div>
          )}

          <div className="submission-actions">
            <button type="submit" className="submit-btn glass-btn" style={{ backgroundColor: "#28a745", color: "#fff" }} disabled={submitting}>
              {submitting ? "Submitting..." : "Submit Now"}
            </button>
            <button type="button" className="cancel-btn glass-btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
          </div>
        </form>
      </div>

      {showSuccess && (
        <div className="submission-success-overlay" role="dialog" aria-modal="true">
          <div className="submission-success-card">
            <h3>Submission uploaded</h3>
            <p>Reference ID: <strong>{successId}</strong></p>
            <div style={{ marginTop: 12 }}>
              <button
                className="submit-btn"
                onClick={() => {
                  setShowSuccess(false);
                  setSuccessId(null);
                  onClose();
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}